/**
 * Modulo per la gestione dell'installazione PWA
 * Gestisce l'evento beforeinstallprompt e verifica se l'app è già installata
 * Supporta sia Android Chrome che iOS Safari
 */

/**
 * Riferimento all'evento beforeinstallprompt salvato
 * @type {BeforeInstallPromptEvent|null}
 */
let deferredPrompt = null;

/**
 * Verifica se l'app è già installata come PWA
 * @returns {boolean} true se l'app è in modalità standalone/fullscreen
 */
function isInstalled() {
	// Controlla display-mode standalone (Android Chrome)
	if (window.matchMedia('(display-mode: standalone)').matches) {
		return true;
	}
	// Controlla display-mode fullscreen
	if (window.matchMedia('(display-mode: fullscreen)').matches) {
		return true;
	}
	// Controlla navigator.standalone (iOS Safari)
	if (window.navigator.standalone === true) {
		return true;
	}
	return false;
}

/**
 * Verifica se il browser è Safari su iOS
 * @returns {boolean} true se è iOS Safari e non è già installata
 */
function isIOSSafari() {
	var ua = navigator.userAgent;
	var isIOS = /iPad|iPhone|iPod/.test(ua);
	var isSafari = /Safari/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
	return isIOS && isSafari && !window.navigator.standalone;
}

/**
 * Inizializza il listener per l'evento beforeinstallprompt
 * Da chiamare all'avvio dell'applicazione
 * @returns {void}
 */
function init() {
	// Controlla se l'evento è già stato catturato dall'HTML (caricato prima del bundle JS)
	if (window.deferredInstallPrompt) {
		deferredPrompt = window.deferredInstallPrompt;
		console.log('PWA: usando evento beforeinstallprompt catturato in precedenza');
	}

	window.addEventListener('beforeinstallprompt', function(e) {
		// Previene il prompt automatico del browser
		e.preventDefault();
		// Salva l'evento per usarlo dopo
		deferredPrompt = e;
		window.deferredInstallPrompt = e;
		console.log('PWA: evento beforeinstallprompt catturato');
	});

	// Listener per quando l'app viene installata
	window.addEventListener('appinstalled', function() {
		console.log('PWA: app installata con successo');
		deferredPrompt = null;
		window.deferredInstallPrompt = null;
	});
}

/**
 * Verifica se l'installazione è disponibile (Android) o se mostrare istruzioni (iOS)
 * @returns {boolean} true se è possibile installare o mostrare istruzioni
 */
function canInstall() {
	if (isInstalled()) {
		return false;
	}
	return deferredPrompt !== null || isIOSSafari();
}

/**
 * Verifica se bisogna mostrare le istruzioni manuali per iOS
 * @returns {boolean} true se è iOS Safari
 */
function needsIOSInstructions() {
	return isIOSSafari() && !isInstalled();
}

/**
 * Mostra il prompt di installazione nativo del browser (solo Android)
 * @returns {Promise<boolean>} true se l'utente ha accettato l'installazione
 */
function showInstallPrompt() {
	return new Promise(function(resolve) {
		if (!deferredPrompt) {
			console.log('PWA: nessun prompt disponibile');
			resolve(false);
			return;
		}

		// Mostra il prompt
		deferredPrompt.prompt();

		// Attendi la risposta dell'utente
		deferredPrompt.userChoice.then(function(choiceResult) {
			if (choiceResult.outcome === 'accepted') {
				console.log('PWA: utente ha accettato installazione');
				resolve(true);
			} else {
				console.log('PWA: utente ha rifiutato installazione');
				resolve(false);
			}
			// Reset del prompt (può essere usato solo una volta)
			deferredPrompt = null;
		});
	});
}

export { init, isInstalled, canInstall, needsIOSInstructions, isIOSSafari, showInstallPrompt };